/**
 * LaunchDarkly configuration for Chime connect and reconnect timing.
 *
 * Flag key: {@link CHIME_MEETING_TIMING_LD_FLAG_KEY} (`chimeMeetingTiming`).
 * Partial JSON from LD is merged with {@link DEFAULT_CHIME_MEETING_TIMING_CONFIG};
 * invalid fields fall back to their defaults.
 */
export const CHIME_MEETING_TIMING_LD_FLAG_KEY = 'chimeMeetingTiming';

/**
 * The SDK only enables its Chrome ICE gathering workaround when its own 5000 ms
 * threshold is below connectionTimeoutMs. At or below it, the workaround is
 * silently disabled, so the connect timeout must stay above this value.
 */
export const CHROME_VPN_ICE_WORKAROUND_THRESHOLD_MS = 5000;

export interface ChimeMeetingTimingConfig {
  connectionTimeoutMs: number;
  reconnectShortBackOffMs: number;
  reconnectLongBackOffMs: number;
}

// The SDK defaults (15000 ms timeout, 1000/5000 ms backoff) leave a stalled
// first attempt waiting ~18 s before a retry succeeds.
export const DEFAULT_CHIME_MEETING_TIMING_CONFIG: ChimeMeetingTimingConfig = {
  connectionTimeoutMs: 10000,
  reconnectShortBackOffMs: 250,
  reconnectLongBackOffMs: 1000,
};

export type ChimeMeetingTimingFlagValue =
  Partial<ChimeMeetingTimingConfig> | undefined | null;

/** Reads the raw JSON value from LaunchDarkly flags. */
export const getChimeMeetingTimingFlag = (
  flags: Record<string, unknown>
): ChimeMeetingTimingFlagValue => {
  const rawValue = flags[CHIME_MEETING_TIMING_LD_FLAG_KEY];
  return rawValue && typeof rawValue === 'object'
    ? (rawValue as ChimeMeetingTimingFlagValue)
    : undefined;
};

const resolvePositiveInteger = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : fallback;

/** Merges a partial LD flag payload with {@link DEFAULT_CHIME_MEETING_TIMING_CONFIG}. */
export const resolveChimeMeetingTimingConfig = (
  flagValue: ChimeMeetingTimingFlagValue
): ChimeMeetingTimingConfig => {
  const defaults = DEFAULT_CHIME_MEETING_TIMING_CONFIG;
  const connectionTimeoutMs = resolvePositiveInteger(
    flagValue?.connectionTimeoutMs,
    defaults.connectionTimeoutMs
  );

  return {
    connectionTimeoutMs:
      connectionTimeoutMs > CHROME_VPN_ICE_WORKAROUND_THRESHOLD_MS
        ? connectionTimeoutMs
        : defaults.connectionTimeoutMs,
    reconnectShortBackOffMs: resolvePositiveInteger(
      flagValue?.reconnectShortBackOffMs,
      defaults.reconnectShortBackOffMs
    ),
    reconnectLongBackOffMs: resolvePositiveInteger(
      flagValue?.reconnectLongBackOffMs,
      defaults.reconnectLongBackOffMs
    ),
  };
};
